import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { useSessionStore } from './session'

/** VAPID-Key kommt als Base64-URL und muss als Bytefolge übergeben werden. */
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}

/**
 * `serviceWorker.ready` bleibt ewig offen, wenn nie ein Worker registriert wurde
 * (z. B. im Dev-Server). Deshalb erst prüfen und zusätzlich begrenzen.
 */
async function readyRegistration(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  const existing = await navigator.serviceWorker.getRegistration()
  if (!existing) return null
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), timeoutMs)),
  ])
}

export type PushSupport = 'supported' | 'no-service-worker' | 'no-push' | 'insecure-context'

function detectSupport(): PushSupport {
  if (!window.isSecureContext) return 'insecure-context'
  if (!('serviceWorker' in navigator)) return 'no-service-worker'
  if (!('PushManager' in window)) return 'no-push'
  return 'supported'
}

export const usePushStore = defineStore('push', () => {
  const support = ref<PushSupport>(detectSupport())
  const permission = ref<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied',
  )
  const subscribed = ref(false)
  const endpoint = ref<string | null>(null)
  const busy = ref(false)
  const error = ref<string | null>(null)
  const testSent = ref(false)

  const isSupported = computed(() => support.value === 'supported')
  const isBlocked = computed(() => permission.value === 'denied')

  async function refresh(): Promise<void> {
    if (!isSupported.value) return
    permission.value = 'Notification' in window ? Notification.permission : 'denied'
    try {
      const registration = await readyRegistration()
      if (!registration) {
        subscribed.value = false
        endpoint.value = null
        return
      }
      const existing = await registration.pushManager.getSubscription()
      subscribed.value = existing !== null
      endpoint.value = existing?.endpoint ?? null
    } catch (caught) {
      error.value = errorMessage(caught)
    }
  }

  async function enable(): Promise<boolean> {
    error.value = null
    if (!isSupported.value) {
      error.value = 'Dieses Gerät oder dieser Browser unterstützt keine Web-Push-Nachrichten.'
      return false
    }

    const session = useSessionStore()
    const vapidKey = session.vapidPublicKey
    if (!vapidKey) {
      error.value = 'Der Server hat keinen Push-Schlüssel hinterlegt. Push ist derzeit nicht möglich.'
      return false
    }

    busy.value = true
    try {
      const granted = await Notification.requestPermission()
      permission.value = granted
      if (granted !== 'granted') {
        error.value =
          granted === 'denied'
            ? 'Benachrichtigungen wurden blockiert. Du kannst sie in den Systemeinstellungen wieder erlauben.'
            : 'Benachrichtigungen wurden nicht erlaubt.'
        return false
      }

      const registration = await readyRegistration()
      if (!registration) {
        error.value =
          'Der Service Worker ist nicht bereit. Lade die Seite neu — im Entwicklungsmodus ist Push nicht verfügbar.'
        return false
      }
      const existing = await registration.pushManager.getSubscription()
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        }))

      const json = subscription.toJSON()
      const keys = json.keys
      if (!json.endpoint || !keys?.p256dh || !keys.auth) {
        error.value = 'Das Push-Abo konnte nicht vollständig gelesen werden.'
        return false
      }

      await api.subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: keys.p256dh, auth: keys.auth },
      })

      subscribed.value = true
      endpoint.value = json.endpoint
      return true
    } catch (caught) {
      error.value = errorMessage(caught)
      return false
    } finally {
      busy.value = false
    }
  }

  async function disable(): Promise<void> {
    error.value = null
    busy.value = true
    try {
      const registration = await readyRegistration()
      const subscription = registration ? await registration.pushManager.getSubscription() : null
      if (subscription) {
        await api.unsubscribePush(subscription.endpoint).catch(() => undefined)
        await subscription.unsubscribe()
      }
      subscribed.value = false
      endpoint.value = null
    } catch (caught) {
      error.value = errorMessage(caught)
    } finally {
      busy.value = false
    }
  }

  async function sendTest(): Promise<void> {
    error.value = null
    testSent.value = false
    busy.value = true
    try {
      await api.testPush()
      testSent.value = true
    } catch (caught) {
      error.value = errorMessage(caught)
    } finally {
      busy.value = false
    }
  }

  return {
    support,
    permission,
    subscribed,
    endpoint,
    busy,
    error,
    testSent,
    isSupported,
    isBlocked,
    refresh,
    enable,
    disable,
    sendTest,
  }
})
